import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "../lib/api";
import { getConsent } from "./CookieConsent";

let _scriptLoaded = false;
function loadAdSenseScript(clientId) {
  if (_scriptLoaded || !clientId || typeof window === "undefined") return;
  if (document.querySelector('script[data-bdd-adsense="1"]')) {
    _scriptLoaded = true;
    return;
  }
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.dataset.bddAdsense = "1";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
  document.head.appendChild(s);
  _scriptLoaded = true;
}

/**
 * Sticky bottom ad anchor (mobile-only). Highest-CTR AdSense format.
 * Dismissable. Hidden on /admin routes.
 * GDPR consent handled by Google's certified CMP automatically.
 */
export default function AnchorAd() {
  const [dismissed, setDismissed] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => api.get("/public-settings").then((r) => r.data),
    staleTime: 60_000,
  });
  const clientId = settings?.adsense_client_id || "";

  // Cookie consent — sticky ad may only load after user grants `ads` consent.
  const [adsConsent, setAdsConsent] = useState(() => {
    const c = getConsent();
    return !!(c && c.ads);
  });
  useEffect(() => {
    const refresh = () => {
      const c = getConsent();
      setAdsConsent(!!(c && c.ads));
    };
    window.addEventListener("bdd_consent_changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("bdd_consent_changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  useEffect(() => {
    if (!clientId || dismissed) return;
    if (!adsConsent) return; // GDPR
    // Wait for the anchor to be visible (mobile media query)
    if (typeof window !== "undefined" && window.matchMedia && !window.matchMedia("(max-width: 767px)").matches) {
      return;
    }
    loadAdSenseScript(clientId);
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      /* will be filled by auto ads */
    }
  }, [clientId, dismissed, adsConsent]);

  // Hide on admin routes
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
    return null;
  }
  if (dismissed || !clientId || !adsConsent) return null;

  return (
    <div
      data-testid="anchor-ad"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-[#0d162d]/95 backdrop-blur-md border-t border-[#1f2c4a] shadow-2xl slide-up"
    >
      <button
        data-testid="anchor-ad-close"
        onClick={() => setDismissed(true)}
        aria-label="Chiudi pubblicità"
        className="absolute -top-3 right-3 w-7 h-7 rounded-full bg-[#1a2647] border border-[#1f2c4a] text-white flex items-center justify-center"
      >
        <X size={14} />
      </button>
      <div className="flex items-center justify-center px-3 py-2">
        <div className="text-[9px] uppercase tracking-[0.25em] text-[#6e7896] mr-3">
          Ad
        </div>
        <ins
          className="adsbygoogle"
          style={{ display: "inline-block", width: "320px", height: "60px" }}
          data-ad-client={clientId}
          data-ad-slot="auto"
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      </div>
    </div>
  );
}
